import React from "react"
import { navigate } from "@reach/router"
import styled from "styled-components"
import colors from "@constants/colors"
import breakpoints from "@constants/breakpoints"
import { body16MainStyles, paragraphStyles } from "@constants/typography"
import { Chevron } from "@components/icons"
import ColorSwatches, {
  ImageSwatch,
  ColorSwatch,
  ActiveIndicator,
} from "@components/ColorSwatches"
import Popover from "@mui/material/Popover"
import DeviceDropdown from "./DeviceDropdown"
import { getIn } from "@utils/index"
import { MobileAndTabletOnly, DesktopOnly } from "@utils/MediaQuery"
import { getProductURL } from "@utils/urls"
import * as R from "ramda"

const Container = styled.div`
  display: flex;
  width: 100%;
  height: auto;
  flex-direction: column;

  @media ${breakpoints.laptop} {
    flex-direction: column-reverse;
  }
`

const Divider = styled.div`
  width: 100%;
  height: 1px;
  transform: translateY(-1px);
  background-color: ${colors.dividerLineGray};
  z-index: 1;
`

const Variant = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  z-index: 2;

  @media ${breakpoints.laptop} {
    justify-content: flex-start;
    overflow: hidden;
    overflow-x: auto;
  }
`

const Devices = styled.div`
  position: relative;
  width: 100%;
  margin-bottom: 8px;
`

const DeviceButton = styled.a<{ canClick?: boolean }>`
  display: flex;
  width: 100%;
  height: 56px;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  height: auto;
  min-height: 56px;
  margin-top: 4px;
  padding: 0 12px;
  border: 1px solid ${colors.lightGray};
  cursor: pointer;
  ${({ canClick }) => (!canClick ? "cursor: initial" : "")}
`

const DeviceButtonText = styled.div`
  ${body16MainStyles}
  display: flex;
  flex-wrap: wrap;
  opacity: 0.9;
  font-weight: 400;
  color: ${colors.black};
`

const SelectText = styled.div`
  ${paragraphStyles}
  margin-bottom: 8px;
  color: ${colors.mediumDarkGray};
`

const buttonProps = `
  display: flex;
  width: 100%;
  height: 56px;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid ${colors.dividerLineGray};
  cursor: pointer;
`

const buttonTextProps = `
  ${body16MainStyles}
  opacity: 0.9;
`

const buttonLightTextProps = `
  ${body16MainStyles}
  color: ${colors.mediumDarkGray};
  opacity: 0.9;
`

const ChevronIcon = styled.div`
  height: 32px;
  width: 32px;
  transform: rotate(90deg);

  @media ${breakpoints.tablet} {
    margin-right: 6px;
  }
`

const ClickableDeviceButton = styled.a<{ canClick?: boolean }>`
  ${buttonProps}
  height: auto;
  min-height: 56px;
  ${({ canClick }) => (!canClick ? "cursor: initial" : "")}
`

const ClickableDeviceButtonText = styled.div`
  ${buttonTextProps}
  display: flex;
  flex-wrap: wrap;
`

const ClickableDeviceButtonTextButton = styled.button`
  ${buttonTextProps}
  flex-wrap: wrap;
  display: flex;
  padding: 0;
  border: none;
  background-color: transparent;
  outline: none;

  &:hover {
    cursor: pointer;
  }

  &:focus {
    outline: none;
  }

  &:active {
    outline: none;
  }
`

const ClickableDeviceButtonTextLink = styled.a`
  ${buttonTextProps}
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  display: flex;
  width: 100%;
  padding: 0;
  border: none;
  background-color: transparent;
  outline: none;
  text-align: left;

  &:hover {
    cursor: pointer;
  }

  &:focus {
    outline: none;
  }

  &:active {
    outline: none;
  }
`

const ClickableDeviceTextWrapper = styled.div`
  padding: 20px;
`

const ClickableDeviceText = styled.div<{ twoColumn: boolean }>`
  ${buttonTextProps}
  flex-shrink: 0;
  width: ${({ twoColumn }) => (twoColumn ? "50%" : "100%")};
  margin: 8px 0;
  font-size: 14px;
  line-height: 16px;
`

const Button = styled.a<{ canClick: boolean }>`
  ${buttonProps}
  ${({ canClick }) => (!canClick ? "cursor: initial" : "")}
`

const ButtonTextWrapper = styled.div`
  display: flex;
`

const ButtonText = styled.div`
  ${buttonTextProps}
`

const ButtonLightText = styled.div`
  ${buttonLightTextProps}
  margin-right: 8px;
`

const ColorWrapper = styled.div`
  display: flex;
  width: 100%;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
`

const ColorTextWrapper = styled.div`
  display: flex;
  padding: 16px 0;
  flex-shrink: 0;
  align-items: center;
`

const SwatchWrapper = styled.div`
  transform: translateX(10px);

  @media ${breakpoints.laptop} {
    transform: none;
  }

  ${ColorSwatch} {
    margin-top: 16px;
  }

  ${ImageSwatch} {
    margin-top: 16px;
  }

  ${ActiveIndicator} {
    height: 2px;
    margin-right: 16px;
  }
`

const Swatches = styled(ColorSwatches)`
  width: auto;
`

interface VariantProps {
  deviceSlug?: string
  deviceName?: string
  sku: string
  price: string
  name: string
  language?: string
  size?: string
  color?: string
  hexString?: string
  swatchImage?: string
  isUnavailable?: boolean
  images: Array<string>
}

interface ProductFormProps {
  productsInSeries: any[]
  brand: string
  device?: string
  multipleDevices?: boolean
  useDeviceAnchor: boolean
  deviceAnchorLabel: string
  variants: VariantProps[]
  currentVariant: VariantProps
  onVariantChange(variant: number)
  onRequestCloseLanguageSelector?: () => void
  onRequestCloseSizeSelector?: () => void
  onClickLanguageSelector?: (productLanguages) => void
  onClickSizeSelector?: (productSizes) => void
}

const ProductForm: React.FC<ProductFormProps> = (props) => {
  const [deviceDropdownIsOpen, setDeviceDropdownIsOpen] = React.useState(false)

  const getClickHandlerForDropdownProduct = (productSlug) => (e) => {
    e.stopPropagation()
    setDeviceDropdownIsOpen(false)
    navigate(getProductURL(productSlug))
  }

  const dropdownProducts: any =
    R.pipe(
      // @ts-ignore
      R.map(({ brand, device, productSlug }) => ({
        brand: brand.name,
        device: device.name,
        onClick: getClickHandlerForDropdownProduct(productSlug),
      })),
      R.sortBy(R.prop("device") as any) as any,
      R.reverse as any
    )(props.productsInSeries as any) || []

  const openLanguageSelector = (productLanguages) => {
    if (!R.isEmpty(productLanguages) && props.onClickLanguageSelector) {
      props.onClickLanguageSelector(productLanguages)
    }
  }

  const [language, setLanguage] = React.useState(undefined)

  const getClickHandlerForLanguage = (language) => (e) => {
    e.stopPropagation()
    const currentColor = getIn(props, "currentVariant.color")
    let variantIndex =
      currentColor &&
      R.findIndex(({ color, language: variantLanguage }) => {
        return color === currentColor && variantLanguage === language
      })(props.variants)

    if (variantIndex < 0) {
      variantIndex = R.findIndex(({ language: variantLanguage }) => {
        return variantLanguage === language
      })(props.variants)
    }

    setLanguage(language)
    props.onVariantChange(variantIndex)

    if (props.onRequestCloseLanguageSelector) {
      props.onRequestCloseLanguageSelector()
    }
  }

  const productLanguages: any = R.pipe(
    R.reject(({ language }: any) => !language) as any,
    R.uniqBy(R.prop("language")) as any,
    // @ts-ignore
    R.map(({ language }) => ({
      language,
      onClick: getClickHandlerForLanguage(language),
    }))
  )(props.variants as any)

  const canClickLanguageButton = productLanguages && productLanguages.length > 1

  const openSizeSelector = (productSizes) => {
    if (!R.isEmpty(productSizes) && props.onClickSizeSelector) {
      props.onClickSizeSelector(productSizes)
    }
  }

  const swatchColors = props.variants.map((variant, index) => ({
    ...R.pick([
      "name",
      "language",
      "size",
      "hexString",
      "swatchImage",
      "isUnavailable",
    ])(variant),
    onClick: () => props.onVariantChange(index),
  }))

  const [size, setSize] = React.useState(undefined)

  const getClickHandlerForSize = (size) => (e) => {
    e.stopPropagation()
    const currentColor = getIn(props, "currentVariant.color")
    let variantIndex =
      currentColor &&
      R.findIndex(({ color, size: variantSize }) => {
        return color === currentColor && variantSize === size
      })(props.variants)

    if (variantIndex < 0) {
      variantIndex = R.findIndex(({ size: variantSize }) => {
        return variantSize === size
      })(props.variants)
    }

    setSize(size)
    props.onVariantChange(variantIndex)

    if (props.onRequestCloseSizeSelector) {
      props.onRequestCloseSizeSelector()
    }
  }

  const productSizes: any = R.pipe(
    R.reject(({ size }: any) => !size) as any,
    R.uniqBy(R.prop("size")) as any,
    // @ts-ignore
    R.map(({ size }) => ({
      size,
      onClick: getClickHandlerForSize(size),
    }))
  )(props.variants as any)

  const canClickSizeButton = productSizes && productSizes.length > 1

  React.useEffect(() => {
    setSize(getIn(productLanguages, "0.language", undefined))
    setSize(getIn(productSizes, "0.size", undefined))
  }, [])

  React.useEffect(() => {
    if (
      props.currentVariant &&
      props.currentVariant.language &&
      props.currentVariant.language !== language
    ) {
      const updatedLanguage = R.find(
        ({ language }) => language === props.currentVariant.language
      )(productLanguages) as any

      if (updatedLanguage) {
        setLanguage(updatedLanguage.language)
      }
    }

    if (
      props.currentVariant &&
      props.currentVariant.size &&
      props.currentVariant.size !== size
    ) {
      const updatedSize = R.find(
        ({ size }) => size === props.currentVariant.size
      )(productSizes) as any

      if (updatedSize) {
        setSize(updatedSize.size)
      }
    }
  }, [props.currentVariant])

  const omitLanguageSize: any = R.map(R.omit(["language", "size"]))

  const getColorsForLanguage = R.pipe(
    R.filter(
      ({ language: languageForColor }: any) => languageForColor === language
    ),
    omitLanguageSize
  )

  const getColorsForSize = R.pipe(
    R.filter(({ size: sizeForColor }: any) => sizeForColor === size),
    omitLanguageSize
  )

  const colorsToShow: any = language
    ? getColorsForLanguage(swatchColors)
    : size
    ? getColorsForSize(swatchColors)
    : omitLanguageSize(swatchColors)

  const allDevices: any =
    props.multipleDevices && props.device && props.device.split("#")

  const [devicePopoverAnchorEl, setDevicePopoverAnchorEl] =
    React.useState<HTMLButtonElement | null>(null)

  const openDevicePopover = (event: React.MouseEvent<HTMLButtonElement>) => {
    setDevicePopoverAnchorEl(event.currentTarget)
  }

  const closeDevicePopover = () => {
    setDevicePopoverAnchorEl(null)
  }

  const showDevicePopover = Boolean(devicePopoverAnchorEl)
  const devicePopoverId = showDevicePopover ? "simple-popover" : undefined

  return (
    <Container>
      <DesktopOnly>
        <Divider />
      </DesktopOnly>
      <Variant>
        {language && (
          <Button
            canClick={canClickLanguageButton}
            onClick={(e) => {
              if (canClickLanguageButton) {
                e.stopPropagation()
                openLanguageSelector(productLanguages)
              }
            }}
          >
            <ButtonTextWrapper>
              <ButtonLightText>Language:</ButtonLightText>
              <ButtonText>{language}</ButtonText>
            </ButtonTextWrapper>
            {canClickLanguageButton && (
              <ChevronIcon>
                <Chevron strokeColor={colors.black} />
              </ChevronIcon>
            )}
          </Button>
        )}
        {size && (
          <Button
            canClick={canClickSizeButton}
            onClick={(e) => {
              if (canClickSizeButton) {
                e.stopPropagation()
                openSizeSelector(productSizes)
              }
            }}
          >
            <ButtonTextWrapper>
              <ButtonLightText>Size:</ButtonLightText>
              <ButtonText>{size}</ButtonText>
            </ButtonTextWrapper>
            {canClickSizeButton && (
              <ChevronIcon>
                <Chevron strokeColor={colors.black} />
              </ChevronIcon>
            )}
          </Button>
        )}
        <ColorWrapper>
          <ColorTextWrapper>
            <ButtonLightText>Color:</ButtonLightText>
            <ButtonText>{props.currentVariant.color}</ButtonText>
          </ColorTextWrapper>
          <SwatchWrapper>
            <Swatches
              colors={colorsToShow}
              activeSwatchName={props.currentVariant.name}
              size="24px"
            />
          </SwatchWrapper>
        </ColorWrapper>
      </Variant>
      <MobileAndTabletOnly>
        <Divider />
      </MobileAndTabletOnly>
      {props.productsInSeries.length > 0 && (
        <Devices>
          <DeviceDropdown
            isOpen={deviceDropdownIsOpen}
            devices={dropdownProducts}
            currentBrand={props.brand}
            currentDevice={props.device || ""}
            onRequestClose={() => setDeviceDropdownIsOpen(false)}
          />
          <SelectText>Select device:</SelectText>
          <DeviceButton
            as="a"
            role="button"
            canClick={true}
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation()
              setDeviceDropdownIsOpen(true)
            }}
          >
            <DeviceButtonText>{props.device}</DeviceButtonText>
            <ChevronIcon>
              <Chevron strokeColor={colors.black} />
            </ChevronIcon>
          </DeviceButton>
        </Devices>
      )}
      {props.productsInSeries.length === 0 && (
        <ClickableDeviceButton as="span" canClick={false}>
          {!props.multipleDevices ? (
            <ClickableDeviceButtonText>
              <ButtonTextWrapper>
                <ButtonLightText>Device:</ButtonLightText>
                <ButtonText>{props.device}</ButtonText>
              </ButtonTextWrapper>
            </ClickableDeviceButtonText>
          ) : props.useDeviceAnchor ? (
            <ClickableDeviceButtonTextLink href="#deviceAnchorLink">
              <ButtonTextWrapper>
                <ButtonLightText>Device:</ButtonLightText>
                <ButtonText>{props.deviceAnchorLabel}</ButtonText>
              </ButtonTextWrapper>
              <ChevronIcon>
                <Chevron strokeColor={colors.black} />
              </ChevronIcon>
            </ClickableDeviceButtonTextLink>
          ) : (
            <>
              <ClickableDeviceButtonTextButton onClick={openDevicePopover}>
                <ButtonTextWrapper>
                  <ButtonLightText>Device:</ButtonLightText>
                  <ButtonText>
                    {`${allDevices[0]} (click to show all (${allDevices.length}) devices)`}
                  </ButtonText>
                </ButtonTextWrapper>
              </ClickableDeviceButtonTextButton>
              <Popover
                id={devicePopoverId}
                anchorEl={devicePopoverAnchorEl}
                open={showDevicePopover}
                onClose={closeDevicePopover}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
              >
                <ClickableDeviceTextWrapper>
                  {allDevices.map((device: any) => (
                    <ClickableDeviceText key={device} twoColumn={false}>
                      {device}
                    </ClickableDeviceText>
                  ))}
                </ClickableDeviceTextWrapper>
              </Popover>
            </>
          )}
        </ClickableDeviceButton>
      )}
    </Container>
  )
}

export default ProductForm
